<template>
  <main
    class="bg-racq-anti-flash-white flex flex-col grow min-h-[calc(100svh-56px-376px)] md:min-h-[calc(100svh-68px-376px)] lg:min-h-[calc(100svh-69px-199px)]"
  >
    <Transition>
      <FullScreenSpinner v-if="fullScreenLoading" />
    </Transition>
    <div class="px-4 pt-6 pb-5 bg-white lg:hidden">
      <h3 v-if="pageTitle" :class="`md:hidden text-racq-navy m-0 text-xl2-bold`">{{ pageTitle }}</h3>
      <FormStepperBar v-if="isStepperVisible" class="mt-2 h-6 w-full justify-stretch items-center" />
    </div>

    <div
      id="base-form-container"
      class="mx-0 md:mx-auto mb-12 md:mb-14 md:max-w-[784px] w-full sm:mt-0 md:mt-8 lg:mt-14"
    >
      <FormStepper
        v-if="isStepperVisible && allPrelimStepsComplete"
        :steps="steps"
        :currentStepIndex="formStore.currentStepIndex"
      />
      <transition name="component-fade" mode="out-in">
        <!-- PRELIM STEPS  -->
        <div v-if="!allPrelimStepsComplete && !showJeopardy" :key="prelimStepIndex">
          <template v-for="(prelimStep, index) in prelimSteps" :key="prelimStep.id">
            <div v-if="index === prelimStepIndex">
              <FormStep :step="prelimStep">
                <component :is="prelimStep.component" :step="prelimStep" />
              </FormStep>
            </div>
          </template>
        </div>
        <!-- PRELIM STEPS END -->

        <!-- PRIMARY STEPS START -->
        <div v-else-if="allPrelimStepsComplete" class="relative">
          <section
            v-for="(step, index) in steps"
            :id="step.id + index.toString()"
            :key="index"
            :class="[
              'js-scrollspy-form-step',
              {
                '!hidden md:!flex': index != formStore.currentStepIndex && step.isVisible,
                '!hidden': !step.isVisible,
              },
              step.isActive ? 'z-[1]' : 'z-[0]',
            ]"
            :dataStepName="step.stepGroupName || step.stepTitle"
            :data-step-active="step.isActive"
          >
            <FormStep :step="step" :stepIndex="index + 1">
              <component :is="step.component" v-if="step.isVisible" :step="step" />
            </FormStep>
          </section>

          <!-- Price Panel [IQFloatingBox] START -->
          <div
            v-if="allPrelimStepsComplete && floatBoxMounted && showFloatingBox"
            class="top-0 w-full xl:w-64 xl:absolute max-xl:!transform-none"
            :style="{
              transform: `translate(${floatBoxTranslate.x}px, ${floatBoxTranslate.y}px)`,
            }"
          >
            <slot name="floatBox"></slot>
          </div>
          <!-- Price Panel [IQFloatingBox] END -->
        </div>
        <!-- PRIMARY STEPS END -->
      </transition>

      <slot v-if="!showJeopardy" name="bottomBox"></slot>

      <JeopardyV2 :fields="jpFields" />
    </div>
    <div class="mx-0 md:mx-auto -mt-5 px-4 lg:px-0 max-w-[784px] w-full">
      <FormStepDisclaimers
        v-if="isDisclaimerVisible"
        :fields="{
          defaultDisclaimer: activeStep?.stepDisclaimer,
          stepTitle: activeStep?.stepTitle,
        }"
      />
    </div>
  </main>

  <ModalDialog v-model="showTooltipModal" :closeOnEsc="true" :showXCloseButton="true" size="tooltip">
    <RichText v-if="tooltipModalTitle" :field="{ value: tooltipModalTitle }" class="sticky-modal-header" />
    <RichText :field="{ value: tooltipModalContent }" class="tooltip-body richtext" />
  </ModalDialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, getCurrentInstance, watch } from 'vue';
import { ComponentFields, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { FormRenderingData } from '@/interfaces/pages/sitecore-props.interface';

import FormStepper from '@/components/FormStepper/FormStepper.vue';
import FormStepperBar from '@/components/FormStepperBar/FormStepperBar.vue';
import FormStep from '@/components/FormStep/FormStep.vue';
import JeopardyV2 from '@/components/JeopardyV2/JeopardyV2.vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import FormStepDisclaimers from '@/components/FormStepDisclaimers/FormStepDisclaimers.vue';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';

import { IFormStep } from '@/interfaces';
import { useJeopardyStore, useFormStore, useLiveChatStore } from '@/store';

// Define props and state
// ------------------------------------------------------------- //
defineProps<{
  rendering: FormRenderingData;
  fields: ComponentFields;
  steps: IFormStep[];
  floatBoxMounted?: boolean;
}>();

const floatBoxTranslate = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const showTooltipModal = ref(false);
const tooltipModalTitle = ref('');
const tooltipModalContent = ref('');

// Initialise stores
// ------------------------------------------------------------- //
const jpStore = useJeopardyStore();
const formStore = useFormStore();
const liveChatStore = useLiveChatStore();

// Computed Properties
// ------------------------------------------------------------- //
const showJeopardy = computed(() => jpStore.showJeopardy);
const prelimSteps = computed(() => formStore.prelimSteps);
const prelimStepIndex = computed(() => formStore.currentPrelimStepIndex);
const allPrelimStepsComplete = computed(() => formStore.allPrelimStepsComplete);
const pageTitle = computed(() => formStore.pageTitle);
const isStepperVisible = computed(() => formStore.isStepperVisible.value);
const activeStep = computed(() =>
  formStore.allPrelimStepsComplete ? formStore.currentStep : formStore.currentPrelimStep
);
const fullScreenLoading = computed(() => formStore.fullScreenLoading);
const showFloatingBox = computed(() => formStore.currentStep?.showIQFloatBox);
const isDisclaimerVisible = computed(() => formStore.isStepDisclaimerVisible);

const app = getCurrentInstance();
const jpFields = app?.appContext.config.globalProperties.$jss.routeData().placeholders['jss-jeopardy'][0].fields;

onMounted(() => {
  // Set the default Jeopardy to display for this form
  jpStore.setDefaultJeopardyPanel(jpFields);
  liveChatStore.setChatEnabled(true);
});

const updateFloatBox = () => {
  const { currentStep, currentStepIndex, inScopeSteps } = formStore;
  const paddingBetweenStepsInPixel = 20;

  if (!currentStep?.id) return;

  const timer = setInterval(() => {
    const getStepContainer = (stepId: string | undefined, stepIndex: number) =>
      stepId ? document.querySelector(`#${stepId + stepIndex.toString()} > div`) : null;

    // Get all previous step containers and accumulate their heights
    let totalHeight = 0;
    for (let i = 0; i < currentStepIndex; i++) {
      const stepId = inScopeSteps[i]?.id;

      const stepContainer = getStepContainer(stepId, i);
      if (stepContainer) {
        totalHeight += stepContainer.clientHeight + paddingBetweenStepsInPixel;
      }
    }

    const currentStepContainer = getStepContainer(currentStep.id, currentStepIndex);

    if (currentStepContainer) {
      clearInterval(timer);
      // Adjust y position with the accumulated height of previous steps
      floatBoxTranslate.value = {
        x: currentStepContainer.clientWidth + 30,
        y: totalHeight,
      };
    }
  }, 100);
};

/**
 * Add as global function to be able to access by tooltip plugin
 */
(window as any).showRACQTooltipModal = (title: string, content: string) => {
  showTooltipModal.value = true;
  tooltipModalTitle.value = title;
  tooltipModalContent.value = content;
};

(window as any).hideRACQTooltipModal = () => {
  showTooltipModal.value = false;
};

watch(
  () => formStore.currentStep, // The property to watch
  (newStep, oldStep) => {
    if (newStep !== oldStep) {
      updateFloatBox(); // Trigger the function when `currentStep` changes
    }
  },
  { immediate: true } // Trigger the watcher immediately after the component mounts
);
</script>
